﻿package org.zq.flvtomp4.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.flvtomp4.converter.ConvertMode
import org.zq.flvtomp4.platform.PlatformFileDrop
import org.zq.flvtomp4.queue.ConvertTask
import org.zq.flvtomp4.queue.OutputLocation
import org.zq.flvtomp4.queue.OverwritePolicy
import org.zq.flvtomp4.queue.TaskStatus
import org.zq.flvtomp4.ui.theme.LocalAppFontFamily
import org.zq.flvtomp4.viewmodel.AppTab
import org.zq.flvtomp4.viewmodel.MainUiState
import org.zq.flvtomp4.viewmodel.MainViewModel

private val PanelBg = Color(0xFFF7F4EF)
private val Accent = Color(0xFF1F6F5B)
private val AccentSoft = Color(0xFFD8EDE6)
private val Border = Color(0xFFD9D2C5)
private val TextMain = Color(0xFF1C1A17)
private val TextMuted = Color(0xFF6B645A)

@Composable
fun MainScreen(viewModel: MainViewModel) {
    val state by viewModel.uiState.collectAsState()
    val history by viewModel.history.collectAsState()
    val fontFamily = LocalAppFontFamily.current
    val textStyle = LocalTextStyle.current.copy(fontFamily = fontFamily)

    CompositionLocalProvider(LocalTextStyle provides textStyle) {
        PlatformFileDrop(
            onFilesDropped = { paths -> viewModel.addFiles(paths) },
            modifier = Modifier.fillMaxSize(),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .background(PanelBg),
            ) {
                TopBar(
                    selectedTab = state.selectedTab,
                    onSelect = viewModel::selectTab,
                )
                HorizontalDivider(color = Border)

                when (state.selectedTab) {
                    AppTab.CONVERT -> ConvertPane(state, viewModel)
                    AppTab.SETTINGS -> SettingsPane(state, viewModel)
                    AppTab.HISTORY -> HistoryPane(history, viewModel)
                    AppTab.ABOUT -> AboutPane()
                }
            }
        }
    }
}

@Composable
private fun TopBar(
    selectedTab: AppTab,
    onSelect: (AppTab) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = "FLV 转 MP4",
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
            color = TextMain,
        )
        Spacer(
            modifier = Modifier.weight(1f),
        )
        TabItem("转换", selectedTab == AppTab.CONVERT) { onSelect(AppTab.CONVERT) }
        TabItem("设置", selectedTab == AppTab.SETTINGS) { onSelect(AppTab.SETTINGS) }
        TabItem("转换记录", selectedTab == AppTab.HISTORY) { onSelect(AppTab.HISTORY) }
        TabItem("关于", selectedTab == AppTab.ABOUT) { onSelect(AppTab.ABOUT) }
    }
}

@Composable
private fun TabItem(label: String, selected: Boolean, onClick: () -> Unit) {
    Text(
        text = label,
        modifier = Modifier
            .padding(horizontal = 10.dp)
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp),
        color = if (selected) Accent else TextMuted,
        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
    )
}

@Composable
private fun ConvertPane(state: MainUiState, viewModel: MainViewModel) {
    Column(modifier = Modifier.fillMaxSize()) {
        DropZone(
            onPickFiles = viewModel::pickAndAddFiles,
        )

        Row(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            TaskListPanel(
                tasks = state.tasks,
                modifier = Modifier.weight(1.4f),
                onCancel = viewModel::cancelTask,
                onRemove = viewModel::removeTask,
                onOpen = viewModel::openOutput,
                onClearCompleted = viewModel::clearCompleted,
            )
            SettingsSidePanel(
                state = state,
                modifier = Modifier.weight(0.9f).widthIn(max = 320.dp),
                onModeChange = viewModel::updateMode,
                onPickOutput = viewModel::pickOutputDirectory,
                onOverwriteChange = viewModel::updateOverwritePolicy,
                onStartAll = viewModel::startAll,
                onOpenFolder = viewModel::openOutputFolder,
            )
        }

        StatusBar(state)
    }
}

@Composable
private fun DropZone(onPickFiles: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.dp)
            .height(120.dp)
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, Border, RoundedCornerShape(12.dp))
            .background(Color.White)
            .clickable(onClick = onPickFiles),
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(
                text = "拖入 FLV 文件",
                fontSize = 18.sp,
                fontWeight = FontWeight.Medium,
                color = TextMain,
            )
            Spacer(modifier = Modifier.height(6.dp))
            Text(
                text = "支持拖拽 / 点击选择 · Web 端转换后自动下载 MP4",
                color = TextMuted,
                fontSize = 13.sp,
            )
        }
    }
}

@Composable
private fun TaskListPanel(
    tasks: List<ConvertTask>,
    modifier: Modifier = Modifier,
    onCancel: (String) -> Unit,
    onRemove: (String) -> Unit,
    onOpen: (ConvertTask) -> Unit,
    onClearCompleted: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxHeight()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, Border, RoundedCornerShape(12.dp))
            .background(Color.White)
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("文件列表", fontWeight = FontWeight.SemiBold, color = TextMain)
            Spacer(modifier = Modifier.weight(1f))
            TextButton(onClick = onClearCompleted) {
                Text("清空已完成")
            }
        }
        Spacer(modifier = Modifier.height(8.dp))
        if (tasks.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text("暂无任务", color = TextMuted)
            }
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                itemsIndexed(tasks, key = { _, task -> task.id }) { index, task ->
                    TaskRow(
                        index = index + 1,
                        task = task,
                        onCancel = { onCancel(task.id) },
                        onRemove = { onRemove(task.id) },
                        onOpen = { onOpen(task) },
                    )
                }
            }
        }
    }
}

@Composable
private fun TaskRow(
    index: Int,
    task: ConvertTask,
    onCancel: () -> Unit,
    onRemove: () -> Unit,
    onOpen: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(AccentSoft.copy(alpha = 0.35f))
            .padding(10.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = index.toString().padStart(2, '0'),
                color = TextMuted,
                modifier = Modifier.width(28.dp),
            )
            Text(
                text = task.fileName,
                modifier = Modifier.weight(1f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = TextMain,
            )
            Text(
                text = statusLabel(task),
                color = statusColor(task.status),
                fontSize = 12.sp,
            )
        }
        if (task.status == TaskStatus.CONVERTING || task.status == TaskStatus.ANALYZING) {
            Spacer(modifier = Modifier.height(6.dp))
            LinearProgressIndicator(
                progress = { task.progress.coerceIn(0f, 1f) },
                modifier = Modifier.fillMaxWidth(),
                color = Accent,
                trackColor = Border,
            )
        }
        if (!task.errorMessage.isNullOrBlank()) {
            Spacer(modifier = Modifier.height(4.dp))
            Text(task.errorMessage.orEmpty(), color = Color(0xFFB42318), fontSize = 12.sp)
        }
        Row {
            if (task.status == TaskStatus.CONVERTING || task.status == TaskStatus.ANALYZING) {
                TextButton(onClick = onCancel) { Text("取消") }
            }
            if (task.status == TaskStatus.COMPLETED) {
                TextButton(onClick = onOpen) { Text("打开目录") }
            }
            if (
                task.status == TaskStatus.COMPLETED ||
                task.status == TaskStatus.FAILED ||
                task.status == TaskStatus.CANCELLED ||
                task.status == TaskStatus.WAITING
            ) {
                TextButton(onClick = onRemove) { Text("移除") }
            }
        }
    }
}

@Composable
private fun SettingsSidePanel(
    state: MainUiState,
    modifier: Modifier = Modifier,
    onModeChange: (ConvertMode) -> Unit,
    onPickOutput: () -> Unit,
    onOverwriteChange: (OverwritePolicy) -> Unit,
    onStartAll: () -> Unit,
    onOpenFolder: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxHeight()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, Border, RoundedCornerShape(12.dp))
            .background(Color.White)
            .padding(14.dp)
            .verticalScroll(rememberScrollState()),
    ) {
        Text("转换设置", fontWeight = FontWeight.SemiBold, color = TextMain)
        Spacer(modifier = Modifier.height(12.dp))

        Text("模式", color = TextMuted, fontSize = 12.sp)
        ModeOption(
            title = "智能转换",
            subtitle = "自动判断转封装或转码",
            mode = ConvertMode.SMART,
            selected = state.settings.mode,
            onModeChange = onModeChange,
        )
        ModeOption(
            title = "极速无损",
            subtitle = "不重新编码，速度最快",
            mode = ConvertMode.REMUX,
            selected = state.settings.mode,
            onModeChange = onModeChange,
        )
        ModeOption(
            title = "高兼容模式",
            subtitle = "H.264 + AAC",
            mode = ConvertMode.TRANSCODE,
            selected = state.settings.mode,
            onModeChange = onModeChange,
        )

        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = "画质：保持原画质（转封装）/ CRF ${state.settings.quality}",
            color = TextMuted,
            fontSize = 12.sp,
        )

        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = when (state.settings.outputLocation) {
                OutputLocation.SAME_AS_SOURCE -> "输出：原文件目录"
                OutputLocation.CUSTOM -> "输出：${state.settings.customOutputDir ?: "未选择"}"
            },
            color = TextMain,
            fontSize = 13.sp,
        )
        TextButton(onClick = onPickOutput) { Text("选择输出目录") }

        Spacer(modifier = Modifier.height(8.dp))
        Text("覆盖策略", color = TextMuted, fontSize = 12.sp)
        OverwriteOption(
            title = "自动重命名",
            subtitle = "遇到同名文件时追加序号",
            selected = state.settings.overwritePolicy == OverwritePolicy.AUTO_RENAME,
            onClick = { onOverwriteChange(OverwritePolicy.AUTO_RENAME) },
        )
        OverwriteOption(
            title = "覆盖已有文件",
            subtitle = "直接覆盖同名 MP4",
            selected = state.settings.overwritePolicy == OverwritePolicy.OVERWRITE,
            onClick = { onOverwriteChange(OverwritePolicy.OVERWRITE) },
        )

        Spacer(modifier = Modifier.height(16.dp))
        Button(
            onClick = onStartAll,
            modifier = Modifier.fillMaxWidth(),
            enabled = state.tasks.any { it.status == TaskStatus.WAITING },
        ) {
            Text("开始全部转换")
        }
        OutlinedButton(
            onClick = onOpenFolder,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text("打开输出目录")
        }
    }
}

@Composable
private fun ModeOption(
    title: String,
    subtitle: String,
    mode: ConvertMode,
    selected: ConvertMode,
    onModeChange: (ConvertMode) -> Unit,
) {
    OverwriteOption(
        title = title,
        subtitle = subtitle,
        selected = selected == mode,
        onClick = { onModeChange(mode) },
    )
}

@Composable
private fun OverwriteOption(
    title: String,
    subtitle: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        RadioButton(selected = selected, onClick = onClick)
        Column {
            Text(title, color = TextMain, fontSize = 14.sp)
            Text(subtitle, color = TextMuted, fontSize = 11.sp)
        }
    }
}

@Composable
private fun StatusBar(state: MainUiState) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White)
            .padding(horizontal = 20.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(24.dp),
    ) {
        Text("总进度 ${state.completedCount}/${state.totalCount}", color = TextMain)
        Text("当前速度 ${state.currentSpeed ?: "-"}", color = TextMain)
        Text("剩余时间 ${state.remainingLabel ?: "--:--"}", color = TextMain)
    }
}

@Composable
private fun SettingsPane(state: MainUiState, viewModel: MainViewModel) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Text("高级设置", style = MaterialTheme.typography.headlineSmall, color = TextMain)
        Text("转封装最大并发：${state.settings.maxConcurrentRemux}", color = TextMain)
        Slider(
            value = state.settings.maxConcurrentRemux.toFloat(),
            onValueChange = {
                viewModel.updateConcurrency(it.toInt(), state.settings.maxConcurrentTranscode)
            },
            valueRange = 1f..8f,
            steps = 6,
        )
        Text("转码最大并发：${state.settings.maxConcurrentTranscode}", color = TextMain)
        Slider(
            value = state.settings.maxConcurrentTranscode.toFloat(),
            onValueChange = {
                viewModel.updateConcurrency(state.settings.maxConcurrentRemux, it.toInt())
            },
            valueRange = 1f..4f,
            steps = 2,
        )
        Text("转码质量 CRF：${state.settings.quality}（越小画质越好）", color = TextMain)
        Slider(
            value = state.settings.quality.toFloat(),
            onValueChange = { viewModel.updateQuality(it.toInt()) },
            valueRange = 18f..28f,
            steps = 9,
        )

        Text("FFmpeg 工具", fontWeight = FontWeight.SemiBold, color = TextMain)
        Text(
            text = "无需系统安装：项目 tools/ffmpeg/windows-x64/ 下已可放置便携版 ffmpeg/ffprobe。",
            color = TextMuted,
            fontSize = 12.sp,
        )
        Text(
            text = "ffmpeg：${state.settings.ffmpegPath.ifBlank { "自动查找 / PATH" }}",
            color = TextMain,
            fontSize = 13.sp,
        )
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(onClick = viewModel::pickFfmpeg) { Text("选择 ffmpeg") }
            OutlinedButton(onClick = viewModel::pickFfprobe) { Text("选择 ffprobe") }
            TextButton(onClick = viewModel::clearFfmpegPaths) { Text("恢复自动") }
        }
        Text(
            text = "ffprobe：${state.settings.ffprobePath.ifBlank { "自动查找 / PATH" }}",
            color = TextMain,
            fontSize = 13.sp,
        )
    }
}

@Composable
private fun HistoryPane(history: List<ConvertTask>, viewModel: MainViewModel) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
    ) {
        Text("最近转换记录", fontWeight = FontWeight.SemiBold, color = TextMain)
        Spacer(modifier = Modifier.height(12.dp))
        if (history.isEmpty()) {
            Text("暂无记录", color = TextMuted)
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                itemsIndexed(history) { _, task ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color.White)
                            .border(1.dp, Border, RoundedCornerShape(8.dp))
                            .padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(task.fileName, color = TextMain)
                            Text(
                                text = task.outputPath,
                                color = TextMuted,
                                fontSize = 12.sp,
                                maxLines = 1,
                            )
                        }
                        TextButton(onClick = { viewModel.openOutput(task) }) {
                            Text("打开")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AboutPane() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text("FLV 转 MP4", style = MaterialTheme.typography.headlineSmall, color = TextMain)
        Text("Compose Multiplatform 桌面版", color = TextMuted)
        Text("转换引擎：FFmpeg / FFprobe（优先 stream copy 转封装）", color = TextMain)
        Text("智能模式会在转封装失败时自动回退到 H.264 + AAC 转码。", color = TextMain)
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            text = "桌面用本地 FFmpeg；Web 可拖拽/选择 FLV，用浏览器 ffmpeg.wasm 转换并下载。",
            color = TextMuted,
        )
    }
}

private fun statusLabel(task: ConvertTask): String {
    return when (task.status) {
        TaskStatus.WAITING -> "等待中"
        TaskStatus.ANALYZING -> "分析中"
        TaskStatus.CONVERTING -> "${(task.progress * 100).toInt()}%  正在转换"
        TaskStatus.COMPLETED -> "已完成"
        TaskStatus.FAILED -> "失败"
        TaskStatus.CANCELLED -> "已取消"
    }
}

private fun statusColor(status: TaskStatus): Color {
    return when (status) {
        TaskStatus.WAITING -> TextMuted
        TaskStatus.ANALYZING -> Accent
        TaskStatus.CONVERTING -> Accent
        TaskStatus.COMPLETED -> Color(0xFF1B7F4B)
        TaskStatus.FAILED -> Color(0xFFB42318)
        TaskStatus.CANCELLED -> TextMuted
    }
}
